/** @format */

import { useEffect, useState } from 'react';

import { Formik } from 'formik';
import * as Yup from 'yup';

import Master from '../Layouts/Master';
import ErrorMessage from '../../Shared/Components/ErrorMessage';
import Loading from '../../Shared/Components/Loading';
import { error, success } from '../../Shared/Components/Toast';
import useApi from '../../Shared/hooks/api/Index';
import { submitGatewayForm } from '../../Shared/utils/helper';

const validationSchema = Yup.object().shape({
  merchant_id: Yup.string()
    .required('کد پذیرنده الزامی است')
    .max(64, 'کد پذیرنده حداکثر ۶۴ کاراکتر است'),
  sep_proxy_url: Yup.string()
    .nullable()
    .max(500, 'آدرس پروکسی خیلی طولانی است')
    .test('is-url', 'آدرس پروکسی معتبر نیست', (value) => {
      if (!value) {
        return true;
      }
      try {
        // eslint-disable-next-line no-new
        new URL(value);
        return true;
      } catch (e) {
        return false;
      }
    }),
  sep_proxy_secret: Yup.string().nullable().max(255),
});

const PaymentSettings = () => {
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [paymentTestLoading, setPaymentTestLoading] = useState(false);
  const [testAmount, setTestAmount] = useState('10000');
  const [testResult, setTestResult] = useState(null);
  const [settings, setSettings] = useState({
    merchant_id: '',
    sep_proxy_url: '',
    has_proxy_secret: false,
    effective_token_url: '',
    using_proxy: false,
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    await useApi.get(
      '/panel/payment-settings',
      {},
      (res) => {
        setSettings(res.data.data);
        setLoading(false);
      },
      () => {
        setLoading(false);
        error('خطا در دریافت تنظیمات درگاه');
      },
    );
  };

  const onSubmitForm = async (values) => {
    setSubmitLoading(true);
    const payload = {
      merchant_id: values.merchant_id,
      sep_proxy_url: values.sep_proxy_url || null,
    };
    if (values.sep_proxy_secret) {
      payload.sep_proxy_secret = values.sep_proxy_secret;
    }

    await useApi.put(
      '/panel/payment-settings',
      payload,
      (res) => {
        setSettings(res.data.data);
        setSubmitLoading(false);
        success(res.data.message || 'تنظیمات ذخیره شد');
      },
      (err) => {
        setSubmitLoading(false);
        const message =
          err?.data?.message ||
          err?.message ||
          'خطا در ذخیره تنظیمات درگاه';
        error(message);
      },
    );
  };

  const onTestConnection = async () => {
    setTestLoading(true);
    setTestResult(null);

    await useApi.post(
      '/panel/payment-settings/test-token',
      {},
      (res) => {
        setTestLoading(false);
        setTestResult(res.data.data);
        success(res.data.message || 'اتصال موفق');
      },
      (err) => {
        setTestLoading(false);
        const data = err?.data || err;
        setTestResult(
          data?.data || {
            ok: false,
            error: data?.message || 'تست اتصال ناموفق بود',
          },
        );
        error(data?.message || data?.data?.error || 'تست اتصال ناموفق بود');
      },
    );
  };

  const onTestPayment = async () => {
    const amount = Number(testAmount);
    if (!Number.isInteger(amount) || amount < 1000 || amount > 1000000) {
      error('مبلغ باید عددی بین ۱٬۰۰۰ تا ۱٬۰۰۰٬۰۰۰ تومان باشد');
      return;
    }

    const gatewayWindowName = `sep-test-${Date.now()}`;
    const gatewayWindow = window.open('', gatewayWindowName);
    setPaymentTestLoading(true);
    await useApi.post(
      '/panel/payment-settings/test-payment',
      { amount },
      (res) => {
        setPaymentTestLoading(false);
        if (!submitGatewayForm(res.data?.data, gatewayWindowName)) {
          gatewayWindow?.close();
          error('اطلاعات انتقال به درگاه ناقص است');
        }
      },
      (err) => {
        gatewayWindow?.close();
        setPaymentTestLoading(false);
        const data = err?.data || err;
        error(data?.message || 'شروع پرداخت آزمایشی ناموفق بود');
      },
    );
  };

  if (loading) {
    return (
      <Master>
        <Loading />
      </Master>
    );
  }

  return (
    <Master>
      <div className='row border rounded mt-3 p-3'>
        <div className='col-12 mb-3'>
          <h1 className='text-dark-mode f-16 font-weight-bold mb-1'>
            تنظیمات درگاه پرداخت سامان
          </h1>
          <p className='f-13 text-muted mb-0'>
            گرفتن token از سرور از طریق پروکسی انجام می‌شود؛ ریدایرکت کاربر همچنان
            مستقیم به شاپرک است.
          </p>
        </div>

        <div className='col-12 mb-3'>
          <div className='alert alert-light f-13 mb-0'>
            <div>
              وضعیت پروکسی:{' '}
              <strong>{settings.using_proxy ? 'فعال' : 'غیرفعال'}</strong>
            </div>
            <div className='mt-1 text-break'>
              آدرس فعلی گرفتن token:{' '}
              <code>{settings.effective_token_url || '—'}</code>
            </div>
          </div>
        </div>

        <div className='col-12'>
          <Formik
            enableReinitialize
            initialValues={{
              merchant_id: settings.merchant_id || '',
              sep_proxy_url: settings.sep_proxy_url || '',
              sep_proxy_secret: '',
            }}
            validationSchema={validationSchema}
            onSubmit={(values) => onSubmitForm(values)}>
            {({ values, errors, handleChange, handleSubmit }) => (
              <form onSubmit={handleSubmit}>
                <div className='form-group'>
                  <label>کد پذیرنده (Terminal / Merchant ID)</label>
                  <input
                    name='merchant_id'
                    type='text'
                    className='form-control'
                    value={values.merchant_id}
                    onChange={handleChange}
                  />
                  <ErrorMessage error={errors.merchant_id} />
                </div>

                <div className='form-group'>
                  <label>آدرس پروکسی SEP</label>
                  <input
                    name='sep_proxy_url'
                    type='text'
                    className='form-control'
                    placeholder='https://sep.dyacopardazesh.com/sep-proxy'
                    value={values.sep_proxy_url}
                    onChange={handleChange}
                  />
                  <small className='text-muted d-block mt-1'>
                    خالی بگذارید تا از مقدار .env استفاده شود؛ برای قطع پروکسی در
                    پنل، مقدار را پاک و ذخیره کنید (در صورت خالی بودن .env).
                  </small>
                  <ErrorMessage error={errors.sep_proxy_url} />
                </div>

                <div className='form-group'>
                  <label>رمز پروکسی (X-Proxy-Secret)</label>
                  <input
                    name='sep_proxy_secret'
                    type='password'
                    className='form-control'
                    autoComplete='new-password'
                    placeholder={
                      settings.has_proxy_secret
                        ? '••••••••  (برای تغییر، مقدار جدید وارد کنید)'
                        : 'مقدار secret را وارد کنید'
                    }
                    value={values.sep_proxy_secret}
                    onChange={handleChange}
                  />
                  <small className='text-muted d-block mt-1'>
                    اگر خالی بماند، secret قبلی تغییر نمی‌کند.
                  </small>
                  <ErrorMessage error={errors.sep_proxy_secret} />
                </div>

                <div className='d-flex flex-wrap align-items-center' style={{ gap: 8 }}>
                  <button
                    type='submit'
                    className='btn btn-success f-13'
                    disabled={submitLoading || testLoading}>
                    {submitLoading ? 'در حال ذخیره...' : 'ذخیره تنظیمات'}
                  </button>
                  <button
                    type='button'
                    className='btn btn-outline-primary f-13'
                    disabled={submitLoading || testLoading}
                    onClick={onTestConnection}>
                    {testLoading ? 'در حال تست...' : 'تست اتصال'}
                  </button>
                </div>
              </form>
            )}
          </Formik>
        </div>

        {testResult && (
          <div className='col-12 mt-3'>
            <div
              className={`alert f-13 mb-0 ${
                testResult.ok ? 'alert-success' : 'alert-danger'
              }`}>
              <div className='font-weight-bold mb-1'>
                {testResult.ok ? 'اتصال موفق' : 'اتصال ناموفق'}
              </div>
              {testResult.ok ? (
                <>
                  <div>پیش‌نمایش token: <code>{testResult.token_preview}</code></div>
                  <div>زمان پاسخ: {testResult.elapsed_ms} ms</div>
                  <div>
                    پروکسی:{' '}
                    {testResult.using_proxy ? 'فعال' : 'غیرفعال (مستقیم شاپرک)'}
                  </div>
                  <div className='text-break'>
                    URL: <code>{testResult.token_url}</code>
                  </div>
                </>
              ) : (
                <>
                  <div>{testResult.error}</div>
                  {testResult.error_code != null && (
                    <div>کد خطا: {String(testResult.error_code)}</div>
                  )}
                  {testResult.elapsed_ms != null && (
                    <div>زمان پاسخ: {testResult.elapsed_ms} ms</div>
                  )}
                  {testResult.token_url && (
                    <div className='text-break'>
                      URL: <code>{testResult.token_url}</code>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        <div className='col-12 mt-4'>
          <hr />
          <h2 className='text-dark-mode f-15 font-weight-bold'>
            تست کامل پرداخت و بازگشت
          </h2>
          <p className='f-13 text-muted'>
            یک پرداخت واقعی با مبلغ کم انجام می‌شود. پس از تأیید موفق درگاه،
            مبلغ به اعتبار کیف پول شما اضافه می‌شود.
          </p>
          <div className='row align-items-end'>
            <div className='col-md-5'>
              <div className='form-group mb-md-0'>
                <label>مبلغ تست (تومان)</label>
                <input
                  type='number'
                  min='1000'
                  max='1000000'
                  step='1000'
                  className='form-control'
                  value={testAmount}
                  onChange={(event) => setTestAmount(event.target.value)}
                />
                <small className='text-muted'>حداقل مبلغ: ۱٬۰۰۰ تومان</small>
              </div>
            </div>
            <div className='col-md-7'>
              <button
                type='button'
                className='btn btn-primary f-13'
                disabled={paymentTestLoading || submitLoading}
                onClick={onTestPayment}>
                {paymentTestLoading
                  ? 'در حال انتقال...'
                  : 'پرداخت آزمایشی در صفحه جدید'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Master>
  );
};

export default PaymentSettings;
